import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Star, Clock, MapPin, Truck, Heart, Moon } from 'lucide-react';
import { useFavorites } from '@/hooks/useFavorites';
import { getOperatingStatus } from '@/lib/operatingHours';

interface StoreCardProps {
  id: string;
  name: string;
  cuisine: string;
  rating: number;
  reviewCount: number;
  deliveryTime: string;
  deliveryFee: number;
  image: string;
  isOpen: boolean;
  badges?: string[];
  distance?: string;
  promo?: string;
}

const StoreCard = ({ 
  id,
  name, 
  cuisine, 
  rating, 
  reviewCount, 
  deliveryTime, 
  deliveryFee, 
  image, 
  isOpen, 
  badges = [],
  distance = "2.1 km",
  promo
}: StoreCardProps) => {
  const { toggleFavorite, isFavorite } = useFavorites();
  const favorite = isFavorite(id);
  const [status, setStatus] = useState(() => getOperatingStatus());

  useEffect(() => {
    const tick = () => setStatus(getOperatingStatus());
    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Store is deliverable only when both the platform window AND the store are open.
  const deliveringNow = status.isOperating && isOpen;

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(id);
  };
  return (
    <div className="card-store group cursor-pointer">
      {/* Image Container */}
      <div className="relative h-40 sm:h-44 md:h-48 overflow-hidden">
        <img 
          src={image.includes('unsplash.com') ? image.replace(/w=\d+/, 'w=400') + '&fm=webp&q=50' : image} 
          alt={name}
          loading="lazy"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        
        {/* Overlay Gradients */}
        <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-transparent to-transparent" />
        
        {/* Status Badge — reflects LUMZA 11PM–7AM operating window */}
        <div className="absolute top-3 left-3 flex flex-col gap-1 items-start">
          <Badge
            variant={deliveringNow ? 'default' : 'secondary'}
            className={
              deliveringNow
                ? 'bg-primary text-primary-foreground shadow-glow-subtle inline-flex items-center gap-1'
                : 'bg-secondary/80 text-secondary-foreground inline-flex items-center gap-1'
            }
          >
            <Moon className="h-3 w-3" />
            {deliveringNow
              ? 'Open now'
              : !status.isOperating
                ? status.detail
                : 'Store offline'}
          </Badge>
        </div>

        {/* Favorite Heart Button */}
        <button
          onClick={handleFavoriteClick}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background transition-all duration-200 hover:scale-110"
          aria-label={favorite ? "Remove from favorites" : "Add to favorites"}
        >
          <Heart 
            className={`h-5 w-5 transition-colors ${
              favorite 
                ? 'fill-primary text-primary' 
                : 'text-muted-foreground hover:text-primary'
            }`}
          />
        </button>

        {/* Promo Badge */}
        {promo && (
          <div className="absolute top-3 right-14">
            <Badge className="bg-destructive text-destructive-foreground animate-pulse">
              {promo}
            </Badge>
          </div>
        )}

        {/* Delivery Info Overlay */}
        <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3">
          <div className="flex items-center justify-between text-white text-xs sm:text-sm">
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="truncate">{deliveryTime}</span>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Truck className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>{deliveryFee === 0 ? 'Free' : `R${deliveryFee}`}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
        {/* Header */}
        <div className="space-y-0.5 sm:space-y-1">
          <h3 className="font-semibold text-base sm:text-lg text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {name}
          </h3>
          <p className="text-muted-foreground text-xs sm:text-sm line-clamp-1">{cuisine}</p>
        </div>

        {/* Rating & Distance */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <Star className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 fill-current" />
            <span className="font-medium text-xs sm:text-sm">{rating}</span>
            <span className="text-muted-foreground text-xs sm:text-sm">({reviewCount})</span>
          </div>
          
          <div className="flex items-center space-x-1 text-muted-foreground text-xs sm:text-sm">
            <MapPin className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
            <span>{distance}</span>
          </div>
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {badges.map((badge, index) => (
              <Badge 
                key={index} 
                variant="outline" 
                className="text-xs border-primary/30 text-primary"
              >
                {badge}
              </Badge>
            ))}
          </div>
        )}

        {/* Interactive Elements */}
        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-muted-foreground">
            Min order: R80
          </div>
          
          {deliveringNow && (
            <div className="flex items-center space-x-1">
              <div className="h-2 w-2 bg-primary rounded-full animate-pulse"></div>
              <span className="text-xs text-primary font-medium">Delivering now</span>
            </div>
          )}
        </div>
      </div>

      {/* Hover Glow Effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        <div className="absolute inset-0 rounded-xl shadow-glow-subtle"></div>
      </div>
    </div>
  );
};

export default StoreCard;